import * as React from "react";
import { graphql, useStaticQuery, Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";

interface FooterProps {
  pathname?: string;
}

interface FooterLink {
  label: string;
  value: string;
  href: string;
}

interface FooterQueryResult {
  allSanityFooter: {
    nodes: {
      logoText?: string | null;
      footerLinks?: FooterLink[] | null;
      copyright?: string | null;
    }[];
  };
}

const Footer = React.forwardRef<HTMLElement, FooterProps>(function Footer(
  { pathname = "/" },
  ref,
) {
  const data = useStaticQuery<FooterQueryResult>(graphql`
    query FooterData {
      allSanityFooter(limit: 1) {
        nodes {
          logoText
          footerLinks {
            label
            value
            href
          }
          copyright
        }
      }
    }
  `);

  const footer = data.allSanityFooter.nodes[0];
  const normalized = pathname.replace(/\/$/, "") || "/";
  const isIntroduction = normalized === "/";
  const isArchitecture = normalized === "/architecture";
  const isPlaybook = normalized === "/playbook";
  const isFoundation = normalized === "/foundation";
  const isVoice = normalized === "/voice";

  return (
    <footer ref={ref} className="bg-space-indigo-900">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-10 py-10 md:grid-cols-2 md:gap-16 px-4 lg:grid-cols-[2fr_1fr]">
          <div className="flex flex-col gap-4">
            <Link to="/" className="block w-fit">
              <span className="sr-only">Home</span>
              <StaticImage
                src="../images/White Logo_821BRIDGE_no_pad.png"
                alt="821BRIDGE"
                className="w-auto"
                imgClassName="w-auto"
                height={20}
                placeholder="none"
              />
            </Link>
            {footer?.logoText && (
              <p className="max-w-md text-base text-space-indigo-300">
                {footer.logoText}
              </p>
            )}
            {footer?.footerLinks && footer.footerLinks.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-base text-space-indigo-300 pt-4">
                {footer.footerLinks.map((link, index) => {
                  const key = `${link.label}-${link.href}`;
                  const isExternal =
                    /^https?:\/\//i.test(link.href) ||
                    link.href.startsWith("mailto:") ||
                    link.href.startsWith("tel:");
                  const linkElement = isExternal ? (
                    <a
                      href={link.href}
                      className="underline transition hover:text-white"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="underline transition hover:text-white"
                    >
                      {link.label}
                    </Link>
                  );

                  return (
                    <React.Fragment key={key}>
                      {index > 0 && (
                        <span className="text-space-indigo-500" aria-hidden>
                          ·
                        </span>
                      )}
                      {linkElement}
                      {link.value && (
                        <>
                          <span className="text-space-indigo-500" aria-hidden>
                            ·
                          </span>
                          <span>{link.value}</span>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>

          <nav
            aria-label="Footer"
            className="flex justify-start md:justify-end"
          >
            <ul className="flex flex-col gap-4 text-base">
              <li>
                <Link
                  to="/"
                  className={`flex items-center gap-2 transition hover:text-white ${
                    isIntroduction
                      ? "!text-white font-semibold footer-link-active"
                      : "text-space-indigo-300"
                  }`}
                >
                  <span
                    className={`footer-dot h-2 w-2 shrink-0 rounded-full ${
                      isIntroduction
                        ? "bg-dusty-olive-600"
                        : "bg-space-indigo-400"
                    }`}
                    aria-hidden
                  />
                  Introduction
                </Link>
              </li>
              <li className="flex flex-col gap-4">
                <Link
                  to="/architecture"
                  className={`flex items-center gap-2 transition hover:text-white ${
                    isArchitecture
                      ? "!text-white font-semibold footer-link-active"
                      : "text-space-indigo-300"
                  }`}
                >
                  <span
                    className={`footer-dot h-2 w-2 shrink-0 rounded-full ${
                      isArchitecture
                        ? "bg-dusty-olive-600"
                        : "bg-space-indigo-500"
                    }`}
                    aria-hidden
                  />
                  Architecture
                </Link>
                <Link
                  to="/playbook"
                  className={`flex items-center gap-2 transition hover:text-white ${
                    isPlaybook
                      ? "!text-white font-semibold footer-link-active"
                      : "text-space-indigo-300"
                  }`}
                >
                  <span
                    className={`footer-dot h-2 w-2 shrink-0 rounded-full ${
                      isPlaybook ? "bg-dusty-olive-600" : "bg-space-indigo-500"
                    }`}
                    aria-hidden
                  />
                  Playbook
                </Link>
                <Link
                  to="/foundation"
                  className={`flex items-center gap-2 transition hover:text-white ${
                    isFoundation
                      ? "!text-white font-semibold footer-link-active"
                      : "text-space-indigo-300"
                  }`}
                >
                  <span
                    className={`footer-dot h-2 w-2 shrink-0 rounded-full ${
                      isFoundation
                        ? "bg-soft-apricot-200"
                        : "bg-space-indigo-500"
                    }`}
                    aria-hidden
                  />
                  Foundation
                </Link>
                <Link
                  to="/voice"
                  className={`flex items-center gap-2 transition hover:text-white ${
                    isVoice
                      ? "!text-white font-semibold footer-link-active"
                      : "text-space-indigo-300"
                  }`}
                >
                  <span
                    className={`footer-dot h-2 w-2 shrink-0 rounded-full ${
                      isVoice ? "bg-dusty-olive-600" : "bg-space-indigo-500"
                    }`}
                    aria-hidden
                  />
                  Voice
                </Link>
              </li>
            </ul>
          </nav>
        </div>

        <div className="border-t border-space-indigo-800 pt-10 pb-8 md:pt-12 px-4">
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-left text-sm text-space-indigo-300">
            {footer?.copyright && <span>{footer.copyright}</span>}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Link
                to="/terms-of-engagement"
                className="underline transition hover:text-white"
              >
                Terms of Engagement
              </Link>
              <span className="text-space-indigo-500" aria-hidden>
                ·
              </span>
              <Link
                to="/privacy-policy"
                className="underline transition text-space-indigo-300 hover:text-white"
              >
                Privacy Policy
              </Link>
              <span className="text-space-indigo-500" aria-hidden>
                ·
              </span>
              <Link
                to="/credits"
                className="underline transition hover:text-white"
              >
                Credits
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});

export default Footer;
